import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// Types
export interface JudgeProfile {
  id: string;
  name: string;
  title?: string;
  organization?: string;
  experience?: string;
  defaultNote?: string;
  photoUrl?: string;
  assignedPosition?: number; // Judge position number
  isTeacherCritique?: boolean; // Flag to indicate if this judge is a Teacher Critique judge
}

export interface Routine {
  id: string;
  routineNumber?: number;
  'Routine #'?: number;
  'Dancer Name'?: string;
  'Dance Title'?: string;
  'Song Length'?: string;
  [key: string]: any;
}

export interface ScoreData {
  judgeId: string;
  routineId: string;
  routineIndex: number;
  scores: Record<string, number>;
  isSubmitted: boolean;
  submittedTime?: string;
  comments?: string;
  simpleScore?: number;
  iChoreographedThis?: boolean;
  notFamilyFriendly?: boolean;
  styleFeedback?: {
    good?: Array<{category: string, criteria: string}>;
    'needs-work'?: Array<{category: string, criteria: string}>;
  };
  routineData?: {
    routineNumber: string;
    routineId: string;
    name: string;
    performerName: string;
    studioCode: string;
    division: string;
    ageGroup: string;
    category: string;
    dancersCount: string;
  };
}

export interface TimerData {
  routineId: string;
  routineIndex: number;
  startTime?: string;
  duration?: number;
  completed?: boolean;
  recordingContinues?: boolean;
}

export interface RoutineChangeData {
  routine: Routine;
  index: number;
}

export interface RecordingMetadata {
  recordingId: string;
  routineId: string;
  routineIndex: number;
  startTime: string;
  status: 'recording' | 'stopped' | 'paused';
}

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopped' | 'error';
export type HardwareStatus = 'disconnected' | 'connected' | 'recording' | 'error';

export interface PollingContextType {
  connected: boolean;
  loading: boolean;
  competitionCode: string;
  role: string | null;
  currentRoutine: Routine | null;
  currentIndex: number;
  scheduleData: Routine[];
  recordingStatus: RecordingStatus;
  recordingMetadata: RecordingMetadata | null;
  hardwareStatus: HardwareStatus;
  connectedJudges: string[];
  judgeProfiles: Record<string, JudgeProfile>;
  judgePositions: Record<string, number>;
  scoreUpdates: Record<string, any>;
  eventTimezone: string | null; // Event timezone from HitchKick data
  // Auto-reconnection status
  isReconnecting: boolean;
  reconnectAttempts: number;
  startSession: (sessionId: string, role: string, scheduleData: any[], judgeId?: string | null, judgeProfile?: JudgeProfile | null, judgePosition?: string | null) => Promise<boolean>;
  leaveSession: () => Promise<boolean>;
  registerJudge: (judgeId: string) => Promise<boolean>;
  setJudgeProfile: (judgeId: string, profile: JudgeProfile) => Promise<boolean>;
  uploadSchedule: (routines: Routine[]) => Promise<boolean>;
  requestSchedule: () => Promise<boolean>;
  changeCurrentRoutine: (data: RoutineChangeData) => Promise<boolean>;
  startRoutineTimer: (data: TimerData) => Promise<boolean>;
  stopRoutineTimer: (data: TimerData) => Promise<boolean>;
  updateScore: (data: ScoreData) => Promise<boolean>;
  submitScore: (data: ScoreData) => Promise<boolean>;
  deleteScore: (judgeId: string, routineId: string) => Promise<boolean>;
  getAllFinalizedScores: () => Promise<boolean>;
  startRecording: (data: any) => Promise<boolean>;
  stopRecording: () => Promise<boolean>;
  restartRecording: (data: any) => Promise<boolean>;
  addRecordingMarker: (note: string) => Promise<boolean>;
  getHardwareStatus: () => Promise<boolean>;
  setVideoSource: (source: string) => boolean;
  setOutputPath: (path: string) => boolean;
  setBitrate: (rate: number) => boolean;
  refreshScoreUpdates: () => Promise<void>;
  refreshGlobalScoreUpdates: () => Promise<void>;
  // Legacy sync functions (deprecated)
  syncWithTabulator: () => void;
  dismissSyncNotification: () => void;
}

export interface PollingProviderProps {
  children: ReactNode;
}

const PollingContext = createContext<PollingContextType | null>(null);

export const usePolling = (): PollingContextType => {
  const context = useContext(PollingContext);
  if (!context) {
    throw new Error('usePolling must be used within a PollingProvider');
  }
  return context;
};

const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';

const apiCall = async (endpoint: string, method: string = 'GET', body?: any) => {
  const url = `${AWS_API_BASE_URL}${endpoint}`;
  
  // Get authentication token using centralized token manager
  let token = null;
  try {
    // Import tokenManager dynamically to get the current token
    const { tokenManager } = await import('../utils/tokenManager');
    token = await tokenManager.getToken();
  } catch (error) {
    console.warn('Could not get auth token:', error);
  }
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  // Add Authorization header if we have a token
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const options: RequestInit = {
    method,
    headers,
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error(`API call failed: ${method} ${endpoint}`, error);
    throw error;
  }
};

export const PollingProvider: React.FC<PollingProviderProps> = ({ children }) => {
  const location = useLocation();
  const [connected, setConnected] = useState<boolean>(false);
  const [competitionCode, setCompetitionCode] = useState<string>('');
  const [currentRoutine, setCurrentRoutine] = useState<Routine | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [scheduleData, setScheduleData] = useState<Routine[]>([]);
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>('idle');
  const [recordingMetadata, setRecordingMetadata] = useState<RecordingMetadata | null>(null);
  const [hardwareStatus, setHardwareStatus] = useState<HardwareStatus>('disconnected');
  const [connectedJudges, setConnectedJudges] = useState<string[]>([]);
  const [judgeProfiles, setJudgeProfiles] = useState<Record<string, JudgeProfile>>({});
  const [judgePositions, setJudgePositions] = useState<Record<string, number>>({});
  const [scoreUpdates, setScoreUpdates] = useState<Record<string, any>>({});
  const [eventTimezone, setEventTimezone] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPollTimestampRef = useRef<number | null>(null); // Track last poll timestamp for delta updates
  const lastScheduleHashRef = useRef<string | null>(null); // Track last schedule hash to detect changes
  const [loading, setLoading] = useState<boolean>(false);
  
  // Role and judge tracking state
  const [role, setRole] = useState<string | null>(null);
  const [judgeId, setJudgeId] = useState<string | null>(null);
  
  // Auto-reconnection state
  const reconnectAttemptsRef = useRef<number>(0);
  const [reconnectAttempts, setReconnectAttempts] = useState<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectionProcessStarted, setReconnectionProcessStarted] = useState<boolean>(false);
  const recoveryMonitorRef = useRef<NodeJS.Timeout | null>(null);
  const maxReconnectAttempts = 5;
  const reconnectDelays = [2000, 5000, 10000, 15000, 30000]; // Progressive delays
  const recoveryCheckInterval = 30000; // Check every 30 seconds after max attempts

  // Extract competition code from URL
  const extractCompetitionCodeFromURL = useCallback(() => {
    const pathSegments = location.pathname.split('/');
    const competitionIndex = pathSegments.indexOf('competition');
    if (competitionIndex !== -1 && pathSegments[competitionIndex + 1]) {
      const potentialCode = pathSegments[competitionIndex + 1];
      // Exclude special paths that are not competition codes
      const excludedPaths = ['portal', 'setup', 'join'];
      if (!excludedPaths.includes(potentialCode)) {
        return potentialCode;
      }
    }
    return null;
  }, [location.pathname]);

  // Helper function to find the first available (unlocked) routine for judges
  const getFirstAvailableRoutineForJudge = useCallback((scheduleData: Routine[], scoreUpdates: Record<string, any>, judgeId: string): number => {
    if (!scheduleData || scheduleData.length === 0 || !judgeId) return 0;
    
    // Check each routine from the beginning
    for (let i = 0; i < scheduleData.length; i++) {
      const routine = scheduleData[i];
      const routineId = routine.number || routine.id || routine.routineNumber;
      
      if (routineId) {
        const scoreKey = `${routineId}_${judgeId}`;
        const scoreData = scoreUpdates[scoreKey];
        
        // A routine is available if this judge hasn't submitted scores for it
        const isLocked = scoreData?.isSubmitted === true;
        
        if (!isLocked) {
          return i; // Return the index of the first unlocked routine
        }
      }
    }
    
    // If all routines are locked, return 0 (first routine)
    return 0;
  }, []);

  // Load competition data from AWS
  const loadCompetitionFromAWS = useCallback(async (competitionCode: string) => {
    if (!competitionCode) return false;
    
    setLoading(true);
    try {
      console.log(`Loading competition ${competitionCode} from AWS...`);
      
      // Reset last poll timestamp and schedule hash for new competition (ensures first poll gets full update)
      lastPollTimestampRef.current = null;
      lastScheduleHashRef.current = null;
      
      // Get competition details (this also tests connection)
      const competitionDetails = await apiCall(`/competition/${competitionCode}/details`);
      console.log('Competition details loaded:', competitionDetails);
      
      // Set connection status to true since API call succeeded
      setConnected(true);
      
      // Set competition state from AWS data
      setCompetitionCode(competitionCode);
      
      // Only update schedule data if we have new data from AWS, otherwise preserve existing data
      setScheduleData(currentScheduleData => {
        if (competitionDetails.scheduleData && competitionDetails.scheduleData.length > 0) {
          return competitionDetails.scheduleData;
        } else {
          return currentScheduleData; // Keep existing data
        }
      });
      
      // Handle current routine separately
      if (competitionDetails.scheduleData && competitionDetails.scheduleData.length > 0) {
        let selectedRoutine;
        let selectedIndex;
        
        if (role === 'judge' && judgeId) {
          // For judges: Find the first available (unlocked) routine
          // Ignore the saved currentRoutine/currentIndex since those are for tabulators
          
          
          selectedIndex = getFirstAvailableRoutineForJudge(
            competitionDetails.scheduleData, 
            competitionDetails.scoreUpdates || {}, 
            judgeId
          );
          selectedRoutine = competitionDetails.scheduleData[selectedIndex] || null;
          
        } else {
          // For tabulators: Use saved currentRoutine/currentIndex from backend
          selectedIndex = competitionDetails.currentIndex || 0;
          selectedRoutine = competitionDetails.currentRoutine || 
                          competitionDetails.scheduleData[selectedIndex] || 
                          null;
        }
        
        setCurrentRoutine(selectedRoutine);
        setCurrentIndex(selectedIndex);
      } else if (competitionDetails.currentRoutine) {
        setCurrentRoutine(competitionDetails.currentRoutine);
      }
      setConnectedJudges(competitionDetails.connectedJudges || []);
      setJudgeProfiles(competitionDetails.judgeProfiles || {});
      setScoreUpdates(competitionDetails.scoreUpdates || {});
      setRecordingStatus(competitionDetails.recordingStatus || 'idle');
      setRecordingMetadata(competitionDetails.recordingMetadata || null);
      setHardwareStatus(competitionDetails.hardwareStatus || 'disconnected');
      setEventTimezone(competitionDetails.eventTimezone || null); // Extract and store eventTimezone
      
      console.log(`Successfully loaded competition ${competitionCode} from AWS`);
      return true;
    } catch (error) {
      console.error(`Failed to load competition ${competitionCode} from AWS:`, error);
      setConnected(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [role, judgeId, getFirstAvailableRoutineForJudge]);

  const pollCompetitionUpdates = useCallback(async (globalScope = false) => {
    if (!competitionCode) return;
    try {
      // Build endpoint with delta update support (since and scheduleHash parameters)
      const now = Date.now();
      const params: string[] = [];
      
      if (globalScope) {
        params.push('global=true');
      }
      if (lastPollTimestampRef.current) {
        params.push(`since=${lastPollTimestampRef.current}`);
      }
      if (lastScheduleHashRef.current) {
        params.push(`scheduleHash=${lastScheduleHashRef.current}`);
      }
      
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const endpoint = `/competition/${competitionCode}/updates${queryString}`;
      
      const response = await apiCall(endpoint);
      
      // Update last poll timestamp for next delta update
      lastPollTimestampRef.current = now;
      
      // Update schedule hash if provided in response
      if (response.scheduleHash) {
        lastScheduleHashRef.current = response.scheduleHash;
      }
      // console.log('🔄 POLLING - Response scoreUpdates:', response.scoreUpdates);
      // console.log('🔄 POLLING - Full response:', response); // Debug log
      // console.log('🔄 POLLING - judgePositions in response:', response.judgePositions);
      // console.log('🔄 POLLING - judgeProfiles in response:', response.judgeProfiles);
      
      // Removed: currentRoutine and currentIndex handling from polling responses
      // Judges and tabulators manage their own routine state locally
      // Only update schedule data if it's included in response (means it changed)
      // Backend omits scheduleData if hash matches, so if present, it's new data
      if (response.scheduleData && response.scheduleData.length > 0) {
        // Only update if actually different (avoid unnecessary re-renders)
        if (JSON.stringify(response.scheduleData) !== JSON.stringify(scheduleData)) {
          setScheduleData(response.scheduleData);
          
          // IMPORTANT: Update currentRoutine with enhanced data from the new scheduleData
          // This ensures the current routine header gets the enhanced fields like studioName
          if (currentRoutine && response.scheduleData.length > currentIndex) {
            const enhancedCurrentRoutine = response.scheduleData[currentIndex];
            if (enhancedCurrentRoutine) {
              setCurrentRoutine(enhancedCurrentRoutine);
            }
          }
        }
      }
      // If scheduleData is not in response, it hasn't changed - keep existing state
      if (response.recordingStatus && response.recordingStatus !== recordingStatus) {
        setRecordingStatus(response.recordingStatus);
      }
      if (response.recordingMetadata && JSON.stringify(response.recordingMetadata) !== JSON.stringify(recordingMetadata)) {
        setRecordingMetadata(response.recordingMetadata);
      }
      if (response.connectedJudges && JSON.stringify(response.connectedJudges) !== JSON.stringify(connectedJudges)) {
        console.log('Updating connected judges:', response.connectedJudges); // Debug log
        setConnectedJudges(response.connectedJudges);
      }
      if (response.judgeProfiles && JSON.stringify(response.judgeProfiles) !== JSON.stringify(judgeProfiles)) {
        console.log('Updating judge profiles:', response.judgeProfiles); // Debug log
        setJudgeProfiles(response.judgeProfiles);
      }
      if (response.judgePositions && JSON.stringify(response.judgePositions) !== JSON.stringify(judgePositions)) {
        console.log('Updating judge positions:', response.judgePositions); // Debug log
        setJudgePositions(response.judgePositions);
      }
      if (response.eventTimezone && response.eventTimezone !== eventTimezone) {
        setEventTimezone(response.eventTimezone);
      }
        // Handle scoreUpdates - support both full and delta updates
        if (response.scoreUpdates) {
          const newKeys = Object.keys(response.scoreUpdates);
          const oldKeys = Object.keys(scoreUpdates);
          const isDeltaUpdate = newKeys.length < oldKeys.length;
          
          // For delta updates: always merge (changed scores are sent)
          // For full updates: check if anything changed before updating
          const shouldUpdate = isDeltaUpdate || (() => {
            // Full update comparison - check if anything changed
            if (newKeys.length !== oldKeys.length) {
              return true;
            }
            // Deep compare entries
            for (const key of newKeys) {
              if (scoreUpdates[key] !== response.scoreUpdates[key]) {
                if (JSON.stringify(scoreUpdates[key]) !== JSON.stringify(response.scoreUpdates[key])) {
                  return true;
                }
              }
            }
            return false;
          })();
          
          if (shouldUpdate) {
          console.log(`🔄 POLLING - ${isDeltaUpdate ? 'Delta' : 'Full'} update: ${newKeys.length} scores`, isDeltaUpdate ? '(merging)' : '(replacing)');
          // Merge updates into existing state (works for both full and delta updates)
          // Delta updates: only changed scores are sent, merged into existing
          // Full updates: all scores are sent, merged effectively replaces all
          setScoreUpdates(prev => ({ ...prev, ...response.scoreUpdates }));
          
          // For judges: Check if we should update current routine based on new score data
          if (role === 'judge' && judgeId && scheduleData && scheduleData.length > 0) {
            const newSelectedIndex = getFirstAvailableRoutineForJudge(scheduleData, response.scoreUpdates, judgeId);
            
            // Only update if the selected routine has changed and we're currently on the first routine
            // This prevents jumping around while the judge is actively working
            if (newSelectedIndex !== currentIndex && currentIndex === 0) {
              setCurrentIndex(newSelectedIndex);
              setCurrentRoutine(scheduleData[newSelectedIndex] || null);
            }
          }
        } else {
          // console.log('🔄 POLLING - No score updates or same as current:', response.scoreUpdates);
          }
        }
    } catch (error) {
      console.error('Polling failed:', error);
      
      // Check if this is a network error
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Distinguish between different types of network errors
      if (errorMessage.includes('ERR_INTERNET_DISCONNECTED')) {
        console.warn('🚫 Complete internet disconnection detected - pausing reconnection attempts');
        setConnected(false);
        // Don't trigger auto-reconnection for complete internet loss
        return;
      }
      
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('CORS') || errorMessage.includes('ERR_NETWORK')) {
        console.warn('⚠️ Network/CORS Error detected - attempting auto-reconnection');
        
        // Set connected to false to indicate polling issues
        setConnected(false);
        
        // Auto-reconnection will be triggered by useEffect when connected becomes false
        // But only if we're not already in a reconnection process
        if (!isReconnecting && !reconnectionProcessStarted) {
          console.log('🔄 Polling detected network failure - will trigger reconnection');
        }
      }
    }
  }, [competitionCode, role, judgePositions, isReconnecting, reconnectionProcessStarted, judgeId, scheduleData, getFirstAvailableRoutineForJudge]);

  const testConnection = useCallback(async () => {
    try {
      // Test connection by getting active competitions
      await apiCall('/competitions/active', 'GET');
      setConnected(true);
      // Reset reconnection state on successful connection
      reconnectAttemptsRef.current = 0;
      setReconnectAttempts(0);
      setIsReconnecting(false);
      setReconnectionProcessStarted(false);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Stop recovery monitoring since we're connected again
      if (recoveryMonitorRef.current) {
        clearInterval(recoveryMonitorRef.current);
        recoveryMonitorRef.current = null;
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      setConnected(false);
      // Don't trigger reconnection here - let the polling failure handler do it
    }
  }, []);

  // Check if we have basic internet connectivity
  const checkInternetConnectivity = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a simple resource that should always be available
      const response = await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      return true;
    } catch (error) {
      console.log('🌐 No internet connectivity detected');
      return false;
    }
  }, []);

  // Recovery monitoring - checks for connection restoration after max attempts reached
  const startRecoveryMonitoring = useCallback(() => {
    if (recoveryMonitorRef.current) {
      clearInterval(recoveryMonitorRef.current);
    }

    console.log('🔍 Starting recovery monitoring - checking every 30 seconds for connection restoration...');
    
    const recoveryCheck = async () => {
      // Only check if we're still disconnected and have a competition code
      if (!connected && competitionCode && !isReconnecting && !reconnectionProcessStarted) {
        console.log('🔍 Recovery check - testing connection...');
        
        try {
          // First check basic internet connectivity
          const hasInternet = await checkInternetConnectivity();
          if (!hasInternet) {
            console.log('🔍 Recovery check - no internet, will try again later');
            return;
          }

          // Try to reconnect to the API
          await apiCall('/competitions/active', 'GET');
          
          // If we get here, the connection is restored!
          console.log('🎉 Recovery successful! Connection restored after max attempts.');
          setConnected(true);
          
          // Reset reconnection state
          reconnectAttemptsRef.current = 0;
          setReconnectAttempts(0);
          
          // Stop recovery monitoring since we're connected again
          if (recoveryMonitorRef.current) {
            clearInterval(recoveryMonitorRef.current);
            recoveryMonitorRef.current = null;
          }
          
        } catch (error) {
          console.log('🔍 Recovery check failed - connection still not available');
        }
      }
    };

    // Start the recovery monitoring interval
    recoveryMonitorRef.current = setInterval(recoveryCheck, recoveryCheckInterval);
  }, [connected, competitionCode, isReconnecting, reconnectionProcessStarted, checkInternetConnectivity, recoveryCheckInterval]);

  // Auto-reconnection logic - unified approach
  const attemptReconnection = useCallback(async () => {
    const currentAttemptCount = reconnectAttemptsRef.current;
    
    // Prevent multiple simultaneous reconnection processes
    if (isReconnecting || reconnectionProcessStarted || currentAttemptCount >= maxReconnectAttempts) {
      return;
    }

    setReconnectionProcessStarted(true);
    setIsReconnecting(true);
    
    // Always increment the counter at the start of each attempt
    const newAttemptCount = currentAttemptCount + 1;
    reconnectAttemptsRef.current = newAttemptCount;
    setReconnectAttempts(newAttemptCount);
    
    console.log(`🔄 Attempting to reconnect... (${newAttemptCount}/${maxReconnectAttempts})`);

    try {
      // First check if we have basic internet connectivity
      const hasInternet = await checkInternetConnectivity();
      if (!hasInternet) {
        console.log('🚫 No internet connection detected');
        throw new Error('No internet connection');
      }

      // Try to reconnect to the API
      await apiCall('/competitions/active', 'GET');
      
      // If we get here, the API is working again
      console.log('✅ Reconnection successful!');
      setConnected(true);
      reconnectAttemptsRef.current = 0;
      setReconnectAttempts(0);
      setIsReconnecting(false);
      setReconnectionProcessStarted(false);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Stop recovery monitoring since we're connected again
      if (recoveryMonitorRef.current) {
        clearInterval(recoveryMonitorRef.current);
        recoveryMonitorRef.current = null;
      }
      
    } catch (error) {
      console.error(`❌ Reconnection attempt ${newAttemptCount} failed:`, error);
      setIsReconnecting(false);
      
      // Try again if we haven't exceeded max attempts
      if (newAttemptCount < maxReconnectAttempts) {
        const delay = reconnectDelays[Math.min(newAttemptCount - 1, reconnectDelays.length - 1)];
        console.log(`⏱️ Retrying in ${delay / 1000} seconds...`);
        
        const timeout = setTimeout(() => {
          setReconnectionProcessStarted(false); // Allow next attempt
          attemptReconnection();
        }, delay);
        
        reconnectTimeoutRef.current = timeout;
      } else {
        console.error('🚫 Max reconnection attempts reached. Starting recovery monitoring...');
        setReconnectionProcessStarted(false);
        
        // Start recovery monitoring to check for connection restoration
        startRecoveryMonitoring();
      }
    }
  }, [isReconnecting, reconnectionProcessStarted, maxReconnectAttempts, reconnectDelays, checkInternetConnectivity, startRecoveryMonitoring]);

  // Trigger reconnection when connection is lost - with debouncing
  useEffect(() => {
    if (!connected && competitionCode && !isReconnecting && !reconnectionProcessStarted && reconnectAttempts < maxReconnectAttempts) {
      console.log('🔄 Connection lost - starting auto-reconnection process...');
      
      // Add a small delay to prevent multiple rapid triggers, then call attemptReconnection
      const timer = setTimeout(() => {
        attemptReconnection();
      }, 500);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [connected, competitionCode, isReconnecting, reconnectionProcessStarted, reconnectAttempts, maxReconnectAttempts, attemptReconnection]);

  useEffect(() => {
    if (competitionCode && connected && !isReconnecting && !reconnectionProcessStarted) {
      // Clear any existing interval first
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      
      // Reduced from 2000ms to 5000ms to prevent HitchKick API rate limiting
      const interval = setInterval(pollCompetitionUpdates, 5000);
      pollingIntervalRef.current = interval;
      
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    } else {
      // Clear polling if we're disconnected or reconnecting
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [competitionCode, connected, isReconnecting, reconnectionProcessStarted, pollCompetitionUpdates]);

  // Auto-load competition data when URL changes
  useEffect(() => {
    const urlCompetitionCode = extractCompetitionCodeFromURL();
    
    // Only load competition data when we have a valid competition code
    // This excludes portal, setup, join, and other special paths
    if (urlCompetitionCode) {
      console.log(`PollingContext: Loading competition data for code: ${urlCompetitionCode}`);
      loadCompetitionFromAWS(urlCompetitionCode);
    }
  }, [location.pathname, extractCompetitionCodeFromURL, loadCompetitionFromAWS]);

  // Legacy connection test for non-competition routes
  useEffect(() => {
    const legacyRoutes = ['/tabulator', '/judge', '/emcee/', '/judge-station/'];
    if (legacyRoutes.some(route => location.pathname.includes(route)) && !location.pathname.includes('/competition/')) {
      testConnection();
    }
  }, [location.pathname, testConnection]);

  const startSession = useCallback(async (sessionId: string, role: string, scheduleData: any[], judgeId: string | null = null, judgeProfile: JudgeProfile | null = null, judgePosition: string | null = null): Promise<boolean> => {
    try {
      console.log(`🚀 STARTING SESSION - Role: ${role}, Competition: ${sessionId}${judgeId ? `, Judge: ${judgeId}` : ''}`);
      
      // Set local state first
      setRole(role);
      setJudgeId(judgeId);
      setCompetitionCode(sessionId);
      setScheduleData(scheduleData || []);
      
      if (role === 'judge' && judgeId && scheduleData && scheduleData.length > 0) {
        // For judges, find the first available routine (no score data available yet during initial session start)
        const routineIndex = 0; // Start with first routine during initial session, will be corrected by loadCompetitionFromAWS
        setCurrentIndex(routineIndex);
        setCurrentRoutine(scheduleData[routineIndex] || null);
      } else {
        // For tabulators and others, use the first routine
        setCurrentIndex(0);
        setCurrentRoutine(scheduleData?.[0] || null);
      }
      
      setConnected(true);
      
      // Register with backend API
      try {
        console.log(`Attempting to join competition ${sessionId} as ${role}${judgeId ? ` (Judge ${judgeId})` : ''}`);
        
        // First, try to join existing competition
        const joinResponse = await apiCall(`/competition/${sessionId}/join`, 'POST', {
          role,
          judgeId,
          judgeProfile,
          judgePosition
        });
        console.log(`Successfully joined existing competition as ${role}:`, joinResponse);
        
        // For manual position assignment, we don't need to redirect
        // The user has already selected their position in the UI
      } catch (joinError) {
        console.log('Competition may not exist, attempting to create it first...', joinError);
        
        // If join fails, try to create the competition first (for tabulator)
        if (role === 'tabulator') {
          try {
            const createResponse = await apiCall('/competition/create-v2', 'POST', {
              competitionCode: sessionId,
              displayName: sessionId,
              eventName: 'Event',
              stageName: 'Stage',
              stageId: sessionId,
              judgeCount: 5,
              scheduleData: scheduleData || []
            });
            console.log(`Successfully created competition ${sessionId}:`, createResponse);
            
                  // Now try to join again
                  const joinResponse = await apiCall(`/competition/${sessionId}/join`, 'POST', {
                    role,
                    judgeId,
                    judgeProfile,
                    judgePosition
                  });
                  console.log(`Successfully joined newly created competition as ${role}:`, joinResponse);
                  
                  // For manual position assignment, we don't need to redirect
                  // The user has already selected their position in the UI
          } catch (createError) {
            console.error('Failed to create competition:', createError);
            // Continue with local session
          }
        } else {
          console.error('Failed to join competition as judge:', joinError);
          // Continue with local session
        }
      }
      
      // Update local state with judge profile if provided
      if (judgeId && judgeProfile) {
        setJudgeProfiles(prev => ({ ...prev, [judgeId]: judgeProfile }));
      }
      
      console.log(`${role} session started successfully`);
      return true;
    } catch (error) {
      console.error('Failed to start session:', error);
      return false;
    }
  }, []);

  const leaveSession = useCallback(async (): Promise<boolean> => {
    try {
      console.log(`Leaving session ${competitionCode}`);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setCompetitionCode('');
      setCurrentRoutine(null);
      setCurrentIndex(0);
      setScheduleData([]);
      setRecordingStatus('idle');
      setRecordingMetadata(null);
      setConnected(false);
      return true;
    } catch (error) {
      console.error('Failed to leave session:', error);
      return false;
    }
  }, [competitionCode]);

  const registerJudge = useCallback(async (judgeId: string): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/register-judge`, 'POST', { judgeId });
      return true;
    } catch (error) {
      console.error('Failed to register judge:', error);
      return false;
    }
  }, [competitionCode]);

  const setJudgeProfile = useCallback(async (judgeId: string, profile: JudgeProfile): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/judge-profile`, 'POST', { judgeId, profile });
      setJudgeProfiles(prev => ({ ...prev, [judgeId]: profile }));
      return true;
    } catch (error) {
      console.error('Failed to set judge profile:', error);
      return false;
    }
  }, [competitionCode]);

  const uploadSchedule = useCallback(async (routines: Routine[]): Promise<boolean> => {
    try {
      // For now, just set the schedule data locally since the backend doesn't support schedule uploads yet
      setScheduleData(routines);
      
      // Set the first routine as current if we have routines
      if (routines.length > 0) {
        setCurrentRoutine(routines[0]);
        setCurrentIndex(0);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to set schedule:', error);
      return false;
    }
  }, []);

  const requestSchedule = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiCall(`/competition/${competitionCode}/schedule`);
      setScheduleData(response.routines || []);
      return true;
    } catch (error) {
      console.error('Failed to request schedule:', error);
      return false;
    }
  }, [competitionCode]);

  const changeCurrentRoutine = useCallback(async (data: RoutineChangeData): Promise<boolean> => {
    // Update local state only - no backend sync needed
    // Judges and tabulators both manage their own routine state locally
        setCurrentRoutine(data.routine);
        setCurrentIndex(data.index);
        return true;
  }, []);

  const startRoutineTimer = useCallback(async (data: TimerData): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/timer/start`, 'POST', data);
      return true;
    } catch (error) {
      console.error('Failed to start routine timer:', error);
      return false;
    }
  }, [competitionCode]);

  const stopRoutineTimer = useCallback(async (data: TimerData): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/timer/stop`, 'POST', data);
      return true;
    } catch (error) {
      console.error('Failed to stop routine timer:', error);
      return false;
    }
  }, [competitionCode]);

  const updateScore = useCallback(async (data: ScoreData): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/score/update`, 'POST', data);
      return true;
    } catch (error) {
      console.error('Failed to update score:', error);
      return false;
    }
  }, [competitionCode]);

  const submitScore = useCallback(async (data: ScoreData): Promise<boolean> => {
    try {
      // Use the existing score/update endpoint with isSubmitted: true for final submissions
      const submitData = {
        ...data,
        isSubmitted: true
      };
      
      await apiCall(`/competition/${competitionCode}/score/update`, 'POST', submitData);
      
      return true;
    } catch (error) {
      console.error('Failed to submit score:', error);
      return false;
    }
  }, [competitionCode]);

  const refreshScoreUpdates = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 Manually refreshing score updates...');
      await pollCompetitionUpdates();
    } catch (error) {
      console.error('Failed to refresh score updates:', error);
    }
  }, [pollCompetitionUpdates]);

  const refreshGlobalScoreUpdates = useCallback(async (): Promise<void> => {
    try {
      console.log('🌍 Manually refreshing GLOBAL score updates...');
      await pollCompetitionUpdates(true); // Request global scope
    } catch (error) {
      console.error('Failed to refresh global score updates:', error);
    }
  }, [pollCompetitionUpdates]);

  const deleteScore = useCallback(async (judgeId: string, routineId: string): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/score/delete`, 'POST', {
        judgeId,
        routineId
      });
      
      // Immediately remove the deleted score from local state for instant UI update
      const scoreKey = `${routineId}_${judgeId}`;
      setScoreUpdates(prevScores => {
        const updatedScores = { ...prevScores };
        delete updatedScores[scoreKey];
        console.log(`🗑️ Immediately removed score key: ${scoreKey} from local state`);
        return updatedScores;
      });
      
      // Also refresh from server to ensure consistency
      console.log('🔄 Score deleted - refreshing score updates from server...');
      await pollCompetitionUpdates();
      
      return true;
    } catch (error) {
      console.error('Failed to delete score:', error);
      return false;
    }
  }, [competitionCode, pollCompetitionUpdates]);

  const getAllFinalizedScores = useCallback(async (): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/scores/finalized`);
      return true;
    } catch (error) {
      console.error('Failed to get finalized scores:', error);
      return false;
    }
  }, [competitionCode]);

  const startRecording = useCallback(async (data: any): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/recording/start`, 'POST', data);
      setRecordingStatus('recording');
      return true;
    } catch (error) {
      console.error('Failed to start recording:', error);
      return false;
    }
  }, [competitionCode]);

  const stopRecording = useCallback(async (): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/recording/stop`, 'POST', {});
      setRecordingStatus('stopped');
      return true;
    } catch (error) {
      console.error('Failed to stop recording:', error);
      return false;
    }
  }, [competitionCode]);

  const restartRecording = useCallback(async (data: any): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/recording/restart`, 'POST', data);
      setRecordingStatus('recording');
      return true;
    } catch (error) {
      console.error('Failed to restart recording:', error);
      return false;
    }
  }, [competitionCode]);

  const addRecordingMarker = useCallback(async (note: string): Promise<boolean> => {
    try {
      await apiCall(`/competition/${competitionCode}/recording/marker`, 'POST', { note });
      return true;
    } catch (error) {
      console.error('Failed to add recording marker:', error);
      return false;
    }
  }, [competitionCode]);

  const getHardwareStatus = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiCall(`/competition/${competitionCode}/hardware/status`);
      setHardwareStatus(response.status || 'disconnected');
      return true;
    } catch (error) {
      console.error('Failed to get hardware status:', error);
      return false;
    }
  }, [competitionCode]);

  const setVideoSource = useCallback((source: string): boolean => {
    console.log('setVideoSource called (legacy method):', source);
    return true;
  }, []);

  const setOutputPath = useCallback((path: string): boolean => {
    console.log('setOutputPath called (legacy method):', path);
    return true;
  }, []);

  const setBitrate = useCallback((rate: number): boolean => {
    console.log('setBitrate called (legacy method):', rate);
    return true;
  }, []);

  // Legacy sync functions (deprecated - no longer used)
  const syncWithTabulator = useCallback(() => {
    // No-op: Sync notifications have been removed
    console.warn('syncWithTabulator is deprecated and no longer functional');
  }, []);

  const dismissSyncNotification = useCallback(() => {
    // No-op: Sync notifications have been removed
    console.warn('dismissSyncNotification is deprecated and no longer functional');
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (recoveryMonitorRef.current) {
        clearInterval(recoveryMonitorRef.current);
      }
    };
  }, []);

  const contextValue: PollingContextType = {
    connected,
    loading,
    competitionCode,
    role,
    currentRoutine,
    currentIndex,
    scheduleData,
    recordingStatus,
    recordingMetadata,
    hardwareStatus,
    connectedJudges,
    judgeProfiles,
    judgePositions,
    scoreUpdates,
    eventTimezone,
    isReconnecting,
    reconnectAttempts,
    startSession,
    leaveSession,
    registerJudge,
    setJudgeProfile,
    uploadSchedule,
    requestSchedule,
    changeCurrentRoutine,
    startRoutineTimer,
    stopRoutineTimer,
    updateScore,
    submitScore,
    deleteScore,
    getAllFinalizedScores,
    startRecording,
    stopRecording,
    restartRecording,
    addRecordingMarker,
    getHardwareStatus,
    setVideoSource,
    setOutputPath,
    setBitrate,
    refreshScoreUpdates,
    refreshGlobalScoreUpdates,
    syncWithTabulator,
    dismissSyncNotification
  };

  return (
    <PollingContext.Provider value={contextValue}>
      {children}
    </PollingContext.Provider>
  );
};

export default PollingProvider;
